/**
 * Stratul de acoperire (survey sau predictie), calculat in Web Worker si
 * desenat intr-un canvas: o celula = un pixel, intins de harta peste plan.
 *
 * Worker-ul se porneste o data, la prima cerere (`new Worker(new URL(...),
 * { type: 'module' })` - fisier propriu, aceeasi adresa, merge cu CSP). Unde
 * nu exista Worker (probele, jsdom), se calculeaza pe loc.
 */
import { useEffect, useState } from 'react'
import { computeCoverage, type CoverageRequest, type CoverageResult } from './coverage'
import type { MapCoverage } from './mapTypes'

let worker: Worker | null = null
/* Un worker care a cazut (politica, browser vechi) nu se mai porneste: se calculeaza pe loc. */
let broken = false
let seq = 0
const waiting = new Map<number, { req: CoverageRequest; resolve: (r: CoverageResult) => void }>()

function run(req: CoverageRequest): Promise<CoverageResult> {
  if (typeof Worker === 'undefined' || broken) return Promise.resolve(computeCoverage(req))
  if (worker === null) {
    try {
      worker = new Worker(new URL('./coverage.worker.ts', import.meta.url), { type: 'module' })
    } catch {
      broken = true
      return Promise.resolve(computeCoverage(req))
    }
    worker.onmessage = (e: MessageEvent<{ id: number; result: CoverageResult }>) => {
      waiting.get(e.data.id)?.resolve(e.data.result)
      waiting.delete(e.data.id)
    }
    worker.onerror = () => {
      broken = true
      worker?.terminate()
      worker = null
      for (const [id, w] of waiting) {
        w.resolve(computeCoverage(w.req))
        waiting.delete(id)
      }
    }
  }
  const id = ++seq
  return new Promise((resolve) => {
    waiting.set(id, { req, resolve })
    worker?.postMessage({ id, req })
  })
}

/** Grila ca imagine (data: URL - `img-src` o permite), sau null daca nu se poate desena. */
export function coverageImage(r: CoverageResult, scale: number): MapCoverage {
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = r.cols
  canvas.height = r.rows
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const data = ctx.createImageData(r.cols, r.rows)
  data.data.set(r.rgba)
  ctx.putImageData(data, 0, 0)
  const size = r.cell * scale
  return { url: canvas.toDataURL('image/png'), extentPx: [0, 0, r.cols * size, r.rows * size] }
}

/** `req` null = stratul e stins. Cererea se compara ca JSON, deci poate fi refacuta la fiecare desenare. */
export function useCoverage(req: CoverageRequest | null, scale: number | null): { image: MapCoverage; busy: boolean } {
  const [image, setImage] = useState<MapCoverage>(null)
  const [busy, setBusy] = useState(false)
  const key = req === null || scale === null ? '' : JSON.stringify(req) + '|' + scale

  useEffect(() => {
    if (req === null || scale === null) {
      setImage(null)
      return
    }
    let alive = true
    setBusy(true)
    void run(req).then((r) => {
      if (!alive) return
      setImage(coverageImage(r, scale))
      setBusy(false)
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { image, busy }
}
